import { useEffect, useState } from 'react';
import { useForm, useFieldArray, useWatch, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import type { Contact, Passenger } from '../../../api/types/common';
import type { CurrentAccount } from '../../../api/types/reports';
import type { BillingInformation } from '../../../api/types/billing';
import type { Traveller } from '../../../api/types/traveller';
import { COUNTRIES } from '../../../config/countries';
import { DIAL_CODES, toInternationalPhone } from '../../../config/phoneRegions';
import { CurrentAccountAutocomplete } from '../../../components/CurrentAccountAutocomplete';
import { TravellerAutocomplete } from '../../../components/TravellerAutocomplete';
import { toLocalPhone, travellerToPassengerPrefill } from '../travellerPrefill';

/** `isInternational` gates whether passport or T.C. kimlik fields are required —
 *  mirrors legacy `FlightHelpers.IsInternational(Model)` branching. */
function buildPassengerSchema(isInternational: boolean) {
  return z
    .object({
      paxType: z.string().min(1),
      firstName: z.string().min(1, 'Ad gerekli'),
      lastName: z.string().min(1, 'Soyad gerekli'),
      gender: z.string().min(1, 'Cinsiyet gerekli'),
      birthDate: z.string().min(1, 'Doğum tarihi gerekli'),
      nationality: z.string().min(1, 'Uyruk gerekli'),
      citizenNo: z.string().optional(),
      passportNo: z.string().optional(),
      passportExpireDate: z.string().optional(),
      frequentFlayerNo: z.string().optional(),
    })
    .superRefine((row, ctx) => {
      if (isInternational) {
        if (!row.passportNo) {
          ctx.addIssue({ code: 'custom', path: ['passportNo'], message: 'Pasaport numarası gerekli' });
        }
        if (!row.passportExpireDate) {
          ctx.addIssue({ code: 'custom', path: ['passportExpireDate'], message: 'Pasaport geçerlilik tarihi gerekli' });
        }
      } else if (row.nationality === 'TR' && !row.citizenNo) {
        ctx.addIssue({ code: 'custom', path: ['citizenNo'], message: 'T.C. Kimlik numarası gerekli' });
      }
    });
}

const contactSchema = z.object({
  name: z.string().min(1, 'İletişim adı gerekli'),
  lastName: z.string().min(1, 'İletişim soyadı gerekli'),
  email: z.string().min(1, 'E-posta gerekli').email('Geçerli bir e-posta girin'),
  phoneRegion: z.string().min(1, 'Bölge gerekli'),
  phone: z.string().min(1, 'Telefon gerekli'),
});

const billingSchema = z.object({
  billingName: z.string().min(1, 'Fatura adı gerekli'),
  /** Cari hesap seçilmemişse bireysel fatura için T.C. Kimlik/Vergi No — legacy'deki
   *  `IdentityNumber` alanının karşılığı, opsiyonel (legacy de zorunlu tutmuyordu). */
  identityOrTaxNo: z.string().optional(),
});

/**
 * `BillingInformation`'ı legacy `CreateBillingInformationRequest`'teki 3 dallı mantığın
 * birebir karşılığıyla kurar: cari hesap seçiliyse onun vergi no'suyla kurumsal fatura;
 * değilse elle girilen T.C./Vergi No ile bireysel fatura; ikisi de yoksa legacy'deki
 * fallback (IfCompany=true, boş TaxNo). `address`/`taxOffice` legacy'deki gibi sabit "B"
 * — backend gerçek bir adres/vergi dairesi metni beklemiyor, sadece boş olmamasını.
 */
function buildBillingInformation(
  currentAccount: CurrentAccount | null,
  billingName: string,
  identityOrTaxNo: string | undefined
): BillingInformation {
  const base = {
    billingName,
    address: 'B',
    taxOffice: 'B',
    autoBill: false,
    serviceFee: 0,
  };
  const currentTaxNo = currentAccount?.taxNumber;
  if (currentTaxNo) {
    return { ...base, ifCompany: true, ifCurrentCodeSet: true, taxNo: currentTaxNo };
  }
  if (identityOrTaxNo) {
    return { ...base, ifCompany: false, ifCurrentCodeSet: false, taxNo: identityOrTaxNo };
  }
  return { ...base, ifCompany: true, ifCurrentCodeSet: false, taxNo: '' };
}

function buildFormSchema(isInternational: boolean) {
  return z.object({
    passengers: z.array(buildPassengerSchema(isInternational)).min(1),
    contact: contactSchema,
    billing: billingSchema,
  });
}

type FormValues = z.infer<ReturnType<typeof buildFormSchema>>;

/** Uncontrolled (`register`) inputs don't shrink their label when RHF's `setValue` fills
 *  them programmatically (autocomplete / cari-hesap seçimi) — MUI only re-checks "filled"
 *  on user input. Forcing `shrink` keeps the label out of the value's way. */
const SHRINK_LABEL = { inputLabel: { shrink: true as const } };

const GENDERS = [
  { value: 'Male', label: 'Erkek' },
  { value: 'Female', label: 'Kadın' },
];

const PAX_TYPE_LABELS: Record<string, string> = {
  ADT: 'Yetişkin Yolcu',
  CHD: 'Çocuk Yolcu',
  CNN: 'Çocuk Yolcu',
  INF: 'Bebek Yolcu',
  STD: 'Öğrenci Yolcu',
  SRC: '+65 Yaş Yolcu',
  MIL: 'Asker Yolcu',
  YTH: 'Genç Yolcu',
};

function paxTypeLabel(paxType: string): string {
  return PAX_TYPE_LABELS[paxType] ?? 'Yolcu';
}

const THY_PROVIDERS = new Set(['TurkishAirlines', 'THYNDC']);

function formatBalance(limit?: number): string {
  if (limit === undefined || limit === null) return '—';
  return `${limit.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺`;
}

const EMPTY_ROW = {
  firstName: '',
  lastName: '',
  gender: '',
  birthDate: '',
  nationality: 'TR',
  citizenNo: '',
  passportNo: '',
  passportExpireDate: '',
  frequentFlayerNo: '',
};

/** Builds one default passenger row per pax item count (e.g. 2x ADT, 1x CHD → 3 rows). */
function buildDefaultPassengers(paxCounts: { paxType: string; count: number }[]): FormValues['passengers'] {
  const rows: FormValues['passengers'] = [];
  paxCounts.forEach(({ paxType, count }) => {
    for (let i = 0; i < count; i += 1) {
      rows.push({ paxType, ...EMPTY_ROW });
    }
  });
  return rows.length ? rows : [{ paxType: 'ADT', ...EMPTY_ROW }];
}

function PassengerIdentityFields({
  control,
  register,
  index,
  isInternational,
  errors,
}: {
  control: ReturnType<typeof useForm<FormValues>>['control'];
  register: ReturnType<typeof useForm<FormValues>>['register'];
  index: number;
  isInternational: boolean;
  errors: ReturnType<typeof useForm<FormValues>>['formState']['errors'];
}) {
  const nationality = useWatch({ control, name: `passengers.${index}.nationality` as const });
  const rowErrors = errors.passengers?.[index];

  if (isInternational) {
    return (
      <>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField
            fullWidth
            size="small"
            label="Pasaport Numarası"
            slotProps={SHRINK_LABEL}
            error={!!rowErrors?.passportNo}
            helperText={rowErrors?.passportNo?.message}
            {...register(`passengers.${index}.passportNo` as const)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField
            fullWidth
            size="small"
            type="date"
            label="Pasaport Geçerlilik Tarihi"
            slotProps={SHRINK_LABEL}
            error={!!rowErrors?.passportExpireDate}
            helperText={rowErrors?.passportExpireDate?.message}
            {...register(`passengers.${index}.passportExpireDate` as const)}
          />
        </Grid>
      </>
    );
  }

  if (nationality === 'TR') {
    return (
      <Grid size={{ xs: 12, sm: 4 }}>
        <TextField
          fullWidth
          size="small"
          label="T.C. Kimlik Numarası"
          slotProps={{ htmlInput: { maxLength: 11 }, inputLabel: { shrink: true } }}
          error={!!rowErrors?.citizenNo}
          helperText={rowErrors?.citizenNo?.message}
          {...register(`passengers.${index}.citizenNo` as const)}
        />
      </Grid>
    );
  }

  return (
    <Grid size={{ xs: 12, sm: 4 }}>
      <TextField
        fullWidth
        size="small"
        label="Pasaport Numarası"
        slotProps={SHRINK_LABEL}
        error={!!rowErrors?.passportNo}
        helperText={rowErrors?.passportNo?.message}
        {...register(`passengers.${index}.passportNo` as const)}
      />
    </Grid>
  );
}

export function AirPassengerForm({
  paxCounts,
  loading,
  isInternational = false,
  provider,
  isReservable = false,
  onSubmit,
}: {
  paxCounts: { paxType: string; count: number }[];
  loading: boolean;
  isInternational?: boolean;
  provider?: string;
  /** `Model.AirBookings[0].FlightSegments[0].IsReservable` — when true, submitting
   *  this form also chains a MakePrebooking call (button reads "Ön Rezervasyon");
   *  otherwise it proceeds straight to finalize/payment ("Ödemeye Devam Et"). */
  isReservable?: boolean;
  onSubmit: (
    passengers: Passenger[],
    contact: Contact,
    currentAccount: CurrentAccount | undefined,
    billingInformation: BillingInformation
  ) => void;
}) {
  const {
    control,
    handleSubmit,
    register,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(buildFormSchema(isInternational)),
    defaultValues: {
      passengers: buildDefaultPassengers(paxCounts),
      contact: { name: '', lastName: '', email: '', phoneRegion: 'TR', phone: '' },
      billing: { billingName: '', identityOrTaxNo: '' },
    },
  });

  const { fields } = useFieldArray({ control, name: 'passengers' });
  const [currentAccount, setCurrentAccount] = useState<CurrentAccount | null>(null);

  /** İletişim ad/soyad her zaman 1. yolcunun ad/soyadıyla aynı — legacy'de iletişim
   *  kişisi ilk yolcuydu. Alanlar salt-okunur; 1. yolcu değişince otomatik güncellenir. */
  const firstPassengerFirstName = useWatch({ control, name: 'passengers.0.firstName' });
  const firstPassengerLastName = useWatch({ control, name: 'passengers.0.lastName' });
  useEffect(() => {
    // validate only once there's a value, so the read-only mirror doesn't flash a
    // "gerekli" error before the 1. yolcu row is filled
    setValue('contact.name', firstPassengerFirstName ?? '', { shouldValidate: !!firstPassengerFirstName });
    setValue('contact.lastName', firstPassengerLastName ?? '', { shouldValidate: !!firstPassengerLastName });
  }, [firstPassengerFirstName, firstPassengerLastName, setValue]);

  const handleCurrentAccountChange = (value: CurrentAccount | null) => {
    setCurrentAccount(value);
    if (value) {
      setValue('billing.billingName', value.title1 || value.title2 || '');
    }
  };

  /** "Kayıtlı Yolcu Ara" seçimi — legacy `allocated.js` `.paxlookup-identity` select
   *  handler'ının karşılığı: seçilen yolcunun bilgilerini o satırın alanlarına yazar,
   *  iletişim e-posta/telefonunu yalnızca boşsa doldurur. İletişim ad/soyadı ise
   *  (1. yolcu doldurulduğunda) yukarıdaki effect ile otomatik senkronlanır. */
  const applyTraveller = (index: number, traveller: Traveller) => {
    const p = travellerToPassengerPrefill(traveller);
    const opts = { shouldValidate: true, shouldDirty: true } as const;
    setValue(`passengers.${index}.firstName`, p.firstName, opts);
    setValue(`passengers.${index}.lastName`, p.lastName, opts);
    setValue(`passengers.${index}.gender`, p.gender, opts);
    setValue(`passengers.${index}.birthDate`, p.birthDate, opts);
    setValue(`passengers.${index}.nationality`, p.nationality, opts);
    setValue(`passengers.${index}.citizenNo`, p.citizenNo, opts);
    setValue(`passengers.${index}.passportNo`, p.passportNo, opts);
    setValue(`passengers.${index}.passportExpireDate`, p.passportExpireDate, opts);
    setValue(`passengers.${index}.frequentFlayerNo`, p.frequentFlayerNo, opts);

    if (traveller.email && !getValues('contact.email')) {
      setValue('contact.email', traveller.email, opts);
    }
    if (traveller.phone && !getValues('contact.phone')) {
      setValue('contact.phone', toLocalPhone(traveller.phone), opts);
    }
  };

  const submit = handleSubmit((values) => {
    const phone = toInternationalPhone(values.contact.phoneRegion, values.contact.phone);

    const passengers: Passenger[] = values.passengers.map((p) => ({
      ...p,
      // Yurtdışı uçuşta kimlik pasaportla verilir — `citizenNo` backend'in beklediği
      // sentinel'e ("00000000000", 11 hane) sabitlenir (legacy `FlightHelpers.
      // CreatePassengers`: `fc["Int"] == "true"` → `CitizenNo = "00000000000"`).
      citizenNo: isInternational ? '00000000000' : p.citizenNo,
      email: values.contact.email,
      phone,
      isChild: p.paxType !== 'ADT',
    }));
    const contact: Contact = {
      name: values.contact.name,
      lastName: values.contact.lastName,
      email: values.contact.email,
      phone,
    };
    const billingInformation = buildBillingInformation(
      currentAccount,
      values.billing.billingName,
      values.billing.identityOrTaxNo
    );
    onSubmit(passengers, contact, currentAccount ?? undefined, billingInformation);
  });

  return (
    <Box component="form" onSubmit={submit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {fields.map((field, index) => (
        <Card key={field.id} variant="outlined">
          <CardContent>
            <Typography variant="subtitle2" gutterBottom>
              {index + 1}. {paxTypeLabel(field.paxType)}
            </Typography>
            {field.paxType === 'STD' && provider && THY_PROVIDERS.has(provider) && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Öğrenciye ait Miles&amp;Miles numarası girilmelidir !
              </Alert>
            )}
            <Box sx={{ mb: 2 }}>
              <TravellerAutocomplete onSelect={(traveller) => applyTraveller(index, traveller)} />
            </Box>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Ad"
                  slotProps={SHRINK_LABEL}
                  error={!!errors.passengers?.[index]?.firstName}
                  helperText={errors.passengers?.[index]?.firstName?.message}
                  {...register(`passengers.${index}.firstName` as const)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Soyad"
                  slotProps={SHRINK_LABEL}
                  error={!!errors.passengers?.[index]?.lastName}
                  helperText={errors.passengers?.[index]?.lastName?.message}
                  {...register(`passengers.${index}.lastName` as const)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Controller
                  control={control}
                  name={`passengers.${index}.gender` as const}
                  render={({ field: f }) => (
                    <TextField
                      {...f}
                      select
                      fullWidth
                      size="small"
                      label="Cinsiyet"
                      error={!!errors.passengers?.[index]?.gender}
                      helperText={errors.passengers?.[index]?.gender?.message}
                    >
                      {GENDERS.map((g) => (
                        <MenuItem key={g.value} value={g.value}>
                          {g.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Doğum Tarihi"
                  slotProps={{ inputLabel: { shrink: true } }}
                  error={!!errors.passengers?.[index]?.birthDate}
                  helperText={errors.passengers?.[index]?.birthDate?.message}
                  {...register(`passengers.${index}.birthDate` as const)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Controller
                  control={control}
                  name={`passengers.${index}.nationality` as const}
                  render={({ field: f }) => (
                    <TextField
                      {...f}
                      select
                      fullWidth
                      size="small"
                      label="Uyruk"
                      error={!!errors.passengers?.[index]?.nationality}
                      helperText={errors.passengers?.[index]?.nationality?.message}
                    >
                      {COUNTRIES.map((c) => (
                        <MenuItem key={c.code} value={c.code}>
                          {c.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>

              <PassengerIdentityFields
                control={control}
                register={register}
                index={index}
                isInternational={isInternational}
                errors={errors}
              />

              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Mil Numarası"
                  slotProps={SHRINK_LABEL}
                  {...register(`passengers.${index}.frequentFlayerNo` as const)}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      ))}

      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle2" gutterBottom>
            Cari Hesap / Fatura Bilgileri
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Kurumsal cari hesap üzerinden ödeme yapılacaksa hesabı seçin (opsiyonel) — seçilirse bakiyesi otomatik
            gösterilir ve fatura adı/vergi no arka planda hesaptan doldurulur. Cari hesap seçilmezse bireysel
            fatura için T.C. Kimlik/Vergi No girebilirsiniz.
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <CurrentAccountAutocomplete value={currentAccount} onChange={handleCurrentAccountChange} />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              {currentAccount ? (
                <TextField
                  fullWidth
                  size="small"
                  label="Bakiye"
                  value={formatBalance(currentAccount.limit)}
                  slotProps={{ input: { readOnly: true }, inputLabel: { shrink: true } }}
                />
              ) : (
                <TextField
                  fullWidth
                  size="small"
                  label="Fatura Adı"
                  slotProps={SHRINK_LABEL}
                  error={!!errors.billing?.billingName}
                  helperText={errors.billing?.billingName?.message}
                  {...register('billing.billingName')}
                />
              )}
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                fullWidth
                size="small"
                label="T.C. Kimlik / Vergi No"
                disabled={!!currentAccount}
                slotProps={SHRINK_LABEL}
                helperText={currentAccount ? 'Cari hesabın vergi no\'su kullanılıyor' : undefined}
                {...register('billing.identityOrTaxNo')}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle2" gutterBottom>
            İletişim Bilgileri
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            İletişim ad ve soyadı her zaman 1. yolcunun bilgileriyle doldurulur.
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Ad"
                value={firstPassengerFirstName ?? ''}
                slotProps={{ input: { readOnly: true }, inputLabel: { shrink: true } }}
                error={!!errors.contact?.name}
                helperText={errors.contact?.name?.message ?? '1. yolcu ile aynı'}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Soyad"
                value={firstPassengerLastName ?? ''}
                slotProps={{ input: { readOnly: true }, inputLabel: { shrink: true } }}
                error={!!errors.contact?.lastName}
                helperText={errors.contact?.lastName?.message ?? '1. yolcu ile aynı'}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="E-posta"
                slotProps={SHRINK_LABEL}
                error={!!errors.contact?.email}
                helperText={errors.contact?.email?.message}
                {...register('contact.email')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <Controller
                control={control}
                name="contact.phoneRegion"
                render={({ field: f }) => (
                  <TextField
                    {...f}
                    select
                    fullWidth
                    size="small"
                    label="Bölge"
                    error={!!errors.contact?.phoneRegion}
                    helperText={errors.contact?.phoneRegion?.message}
                  >
                    {COUNTRIES.map((c) => (
                      <MenuItem key={c.code} value={c.code}>
                        {c.name} ({DIAL_CODES[c.code] ?? ''})
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                fullWidth
                size="small"
                label="Telefon"
                placeholder="05388828249"
                slotProps={SHRINK_LABEL}
                error={!!errors.contact?.phone}
                helperText={errors.contact?.phone?.message}
                {...register('contact.phone')}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Box>
        <Button type="submit" variant="contained" disabled={loading}>
          {loading ? 'İşleniyor…' : isReservable ? 'Ön Rezervasyon' : 'Ödemeye Devam Et'}
        </Button>
      </Box>
    </Box>
  );
}
